import { TextAreaHelper } from '@components/VacancyContent/Blocks/ApplicationForm/Form/InputFields/TextAreaHelper';
import { Flex, freddyTheme } from '@coolblue-development/freddy';
import { InputField } from '@sharedTypes';
import React, { PropsWithChildren } from 'react';

import { FileUploadHelper } from './FileUploadHelper';
import { RadioHelper } from './RadioHelper';
import { TextInputHelper } from './TextInputHelper';

type Props = {
  inputFields: InputField[];
  isLoading: boolean;
};

export const InputFields = ({ inputFields, isLoading, children }: PropsWithChildren<Props>) => {
  const DataToInputField = (inputField: InputField) => {
    switch (inputField.type) {
      case 'group':
        return (
          <Flex
            key={inputField.group.toString()}
            wrap='wrap'
            gap={{ xs: 500, md: 800 }}
            sx={{
              '& > *': {
                width: `calc(50% - calc(${freddyTheme.spacing[500]} / 2))`
              },
              [freddyTheme.breakpoint.minWidth('md')]: {
                '& > *': {
                  width: `calc(50% - calc(${freddyTheme.spacing[800]} / 2))`
                }
              }
            }}
          >
            {inputField.group.map(DataToInputField)}
          </Flex>
        );
      case 'radio':
        return (
          <RadioHelper
            key={inputField.id}
            direction={inputField.direction}
            name={inputField.id}
            radioFields={inputField.radioFields}
            label={inputField.label}
            rules={inputField.rules}
            disable={isLoading}
          />
        );
      case 'textarea':
        return (
          <TextAreaHelper
            key={inputField.id}
            name={inputField.id}
            label={inputField.label}
            placeholder={inputField.placeholder}
            rules={inputField.rules}
          />
        );
      case 'file':
        return (
          <FileUploadHelper
            key={inputField.id}
            name={inputField.id}
            documentType={inputField.documentType}
            label={inputField.label}
            buttonText={inputField.title}
            rules={inputField.rules}
          />
        );
      default:
        return (
          <TextInputHelper
            key={inputField.id}
            name={inputField.id}
            type={inputField.type}
            label={inputField.label}
            placeholder={inputField.placeholder}
            disable={isLoading}
            rules={inputField.rules}
          />
        );
    }
  };

  const lastDefaultQuestionIndex =
    inputFields.findLastIndex(input => input.questionType === 'default') + 1;

  return (
    <Flex
      direction='column'
      gap={{ xs: 400, md: `${freddyTheme.spacing[500]} ${freddyTheme.spacing[800]}` }}
      sx={{
        [freddyTheme.breakpoint.minWidth('md')]: {
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridAutoFlow: 'column',
          '& > :nth-of-type(1)': {
            gridColumn: '1 / span 2'
          },
          '& > :nth-of-type(2n)': {
            gridColumn: '1'
          }
        }
      }}
    >
      {inputFields.slice(0, lastDefaultQuestionIndex).map(DataToInputField)}
      {children}
      {inputFields.slice(lastDefaultQuestionIndex).map(DataToInputField)}
    </Flex>
  );
};
